import { useEffect, useState } from "react";
import {
  useGetMe,
  useUpdateMePreferences,
  useGetSenderAddress,
  useGetBrandSettings,
  getGetMeQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, UserCog } from "lucide-react";

const LOCAL_PART_RE = /^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$/;

/**
 * Per-user "My sending defaults" card. Always editable by the signed-in
 * user, regardless of whether the brand-wide settings below are
 * admin-managed. When set, these values pre-fill the From name and From
 * alias on the compose page (and override the brand-wide defaults for
 * this user's outgoing mail).
 *
 * - `defaultFromName` — display name shown to recipients (e.g. "John Doe").
 *   Empty → falls back to brand-wide default → final fallback "Support Team".
 * - `defaultFromLocalPart` — the part before `@<verified-domain>`. Empty →
 *   falls back to brand-wide default → final fallback to the verified
 *   shared sender (typically "support"). Validated against the same
 *   regex the server uses on the wire.
 */
export function MySendingDefaultsCard() {
  const queryClient = useQueryClient();
  const { data: me } = useGetMe();
  const { data: brand } = useGetBrandSettings();
  const { data: sender } = useGetSenderAddress();
  const updateMutation = useUpdateMePreferences();

  const [fromName, setFromName] = useState("");
  const [fromLocalPart, setFromLocalPart] = useState("");

  // Reset the local form state whenever the server-side row changes
  // (initial load, or another tab's save). We mirror null → "" so the
  // input is controlled. The "" sentinel doubles as the wire signal to
  // clear the saved value when submitting.
  useEffect(() => {
    if (me) {
      setFromName(me.defaultFromName ?? "");
      setFromLocalPart(me.defaultFromLocalPart ?? "");
    }
  }, [me]);

  const fromEmail = sender?.fromEmail ?? "";
  const fromDomain =
    brand?.fromDomain ||
    (fromEmail.includes("@") ? fromEmail.split("@")[1] : "");
  const verifiedDefaultLocalPart = fromEmail.includes("@")
    ? fromEmail.split("@")[0]
    : "";

  const trimmedLocalPart = fromLocalPart.trim().toLowerCase();
  const localPartInvalid =
    trimmedLocalPart.length > 0 && !LOCAL_PART_RE.test(trimmedLocalPart);

  const dirty =
    fromName.trim() !== (me?.defaultFromName ?? "") ||
    trimmedLocalPart !== (me?.defaultFromLocalPart ?? "");

  // Live preview of the From line as recipients will see it. Mirrors the
  // server's fallback chain: this card → brand-wide → verified shared.
  const previewName =
    fromName.trim() || brand?.fromName || "Support Team";
  const previewLocal =
    (trimmedLocalPart && !localPartInvalid && trimmedLocalPart) ||
    brand?.fromLocalPart ||
    verifiedDefaultLocalPart ||
    "support";
  const previewFrom = fromDomain
    ? `${previewName} <${previewLocal}@${fromDomain}>`
    : previewName;

  const onSave = async () => {
    if (localPartInvalid) {
      toast.error("Please fix the From alias before saving.");
      return;
    }
    try {
      await updateMutation.mutateAsync({
        data: {
          // Empty string clears the saved value on the server (falls back
          // to brand-wide). A trimmed non-empty string saves it.
          defaultFromName: fromName.trim(),
          defaultFromLocalPart: trimmedLocalPart,
        },
      });
      toast.success("Sending defaults saved");
      queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
    } catch (e: unknown) {
      toast.error(apiErrorMessage(e, "Failed to save sending defaults"));
    }
  };

  const onClear = () => {
    setFromName("");
    setFromLocalPart("");
  };

  return (
    <Card data-testid="card-my-sending-defaults">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <UserCog className="w-5 h-5" /> My sending defaults
        </CardTitle>
        <CardDescription>
          These pre-fill the From name and From alias on the compose page
          for your account only. Other people in this workspace are not
          affected. Leave a field blank to fall back to the brand-wide
          default below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="my-default-from-name">My From name</Label>
          <Input
            id="my-default-from-name"
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            placeholder={brand?.fromName || "Support Team"}
            maxLength={120}
            data-testid="input-my-default-from-name"
          />
          <p className="text-xs text-muted-foreground">
            The friendly name shown before the email address.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="my-default-from-local-part">My From alias</Label>
          <div className="flex items-stretch rounded-md border border-input focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background overflow-hidden">
            <Input
              id="my-default-from-local-part"
              value={fromLocalPart}
              onChange={(e) =>
                setFromLocalPart(e.target.value.toLowerCase())
              }
              placeholder={
                brand?.fromLocalPart || verifiedDefaultLocalPart || "support"
              }
              maxLength={64}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 font-mono"
              aria-invalid={localPartInvalid || undefined}
              data-testid="input-my-default-from-local-part"
            />
            {fromDomain && (
              <span className="flex items-center px-3 bg-muted/40 text-sm text-muted-foreground font-mono border-l border-input">
                @{fromDomain}
              </span>
            )}
          </div>
          {localPartInvalid && (
            <p
              className="text-xs text-red-600 dark:text-red-400"
              data-testid="input-my-default-from-local-part-error"
            >
              Use 1-64 lowercase letters, digits, ".", "_" or "-". Must
              start and end with a letter or digit.
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            The part before <span className="font-mono">@{fromDomain || "your-domain"}</span>.
            The domain is fixed to your verified shared sender.
          </p>
        </div>

        <div
          className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs font-mono truncate"
          title={previewFrom}
          data-testid="my-default-from-preview"
        >
          {previewFrom}
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button
            type="button"
            onClick={onSave}
            disabled={
              !dirty || localPartInvalid || updateMutation.isPending
            }
            data-testid="btn-save-my-sending-defaults"
          >
            {updateMutation.isPending && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            Save my defaults
          </Button>
          {(fromName !== "" || fromLocalPart !== "") && (
            <Button
              type="button"
              variant="ghost"
              onClick={onClear}
              disabled={updateMutation.isPending}
              data-testid="btn-clear-my-sending-defaults"
            >
              Clear fields
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
